<template>
  <div class="flex flex-col gap-16">
    <h3 class="text-static-default-mid body-2">{{ title }}</h3>
    <ul class="flex flex-col">
      <li
        v-for="(suggestion, index) in suggestions"
        :key="suggestion.title"
        class="group relative"
        :data-cnstrc-item-name="suggestion.title"
        data-cnstrc-item-section="Search Suggestions"
        data-test="search-bar-dropdown-item"
      >
        <RevButtonBase
          class="rounded-sm body-1-bold block p-8 pr-32 hover:bg-static-default-min-hover hover:no-underline"
          :to="suggestion.url"
          :underlined="false"
          @click="handleSuggestionLinkClick(suggestion, index)"
        >
          <span>
            {{ suggestion.title }}
          </span>
        </RevButtonBase>
        <RevButtonIcon
          class="absolute right-8 top-8 h-24 w-24 group-focus-within:visible group-hover:visible motion-safe:transition motion-safe:duration-300 motion-safe:ease-in md:invisible"
          :icon="IconArrowUpLeft"
          variant="ghost"
          @click.stop="handleArrowUpLeftClick(suggestion)"
        />
      </li>

      <li v-if="inputValue">
        <RevButtonBase
          class="rounded-sm body-1-bold flex items-center gap-8 p-8 hover:bg-static-default-min-hover hover:no-underline"
          :title="inputValue"
          :to="
            resolveLocalizedRoute({
              name: PRODUCT_LIST_ROUTES.SEARCH_RESULTS,
              query: {
                q: inputValue,
              },
            })
          "
          @click="handleInputSearchClick"
        >
          <IconSearch class="h-24 w-24 flex-none" />
          <span class="truncate">"{{ inputValue }}"</span>
        </RevButtonBase>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { IconArrowUpLeft } from '@ds/icons/IconArrowUpLeft'
import { IconSearch } from '@ds/icons/IconSearch'

import { useRouteLocationWithLocale } from '~/composables/useRouteLocationWithLocale'
import { ROUTES as PRODUCT_LIST_ROUTES } from '~/scopes/product-list/route-names'
import type { Link } from '~/scopes/search/algolia/algoliaFunctions'

const props = defineProps<{
  name: 'suggestions' | 'popularSearches'
  inputValue?: string
  suggestions: Array<Link>
  title: string
}>()

const emits = defineEmits(['select', 'submit'])

const { trackFormSubmit } = useTracking()

const resolveLocalizedRoute = useRouteLocationWithLocale()

function handleSuggestionLinkClick(suggestion: Link, index: number) {
  emits('submit')
  trackFormSubmit({
    name: suggestion.title,
    payload: { index_used: index, search_type: props.name },
    zone: 'header_searchbar',
  })
}

function handleArrowUpLeftClick(suggestion: Link) {
  emits('select', suggestion.title)
}

function handleInputSearchClick() {
  emits('submit')
  trackFormSubmit({
    name: props.inputValue as string,
    payload: { index_used: -1, search_type: 'userInput' },
    zone: 'header_searchbar',
  })
}
</script>
